import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, PractitionerCard, Chips } from "@/components/Shared";
import { HairHero, HairBreadcrumb, HairBand, HairEyebrow, HairHeading, Bullets, Paras, HairFaq, NumberedColumns, HairCTABand } from "@/components/HairShared";
import { FloatingBar, FirstVisitBox } from "@/components/Landing";
import { getConcern, concernPath } from "@/data/concerns";
import { getTeamMember, TREATMENTS } from "@/data/content";
import { pickPrimary, offerForConcern, fillFaqs, CONCERN_OVERRIDES } from "@/data/landing";
import { HAIR_BREADCRUMBS, HAIR_TREATMENT_CARDS, MALE_PATTERN_EXTRA, FEMALE_THINNING_EXTRA } from "@/data/hair";
import { concernImage } from "@/data/concernImages";
import { NorwoodStageArt } from "@/components/NorwoodScale";
import NotFound from "@/pages/NotFound";

const ScaleStages = ({ data, eyebrow = "The Norwood scale", testId = "norwood" }) => (
  <HairBand dark testId={`${testId}-stages`}>
    <Reveal className="mb-10">
      <HairEyebrow className="mb-3">{eyebrow}</HairEyebrow>
      <HairHeading dark>{data.heading}</HairHeading>
      <p className="text-warm-white/80 mt-6 max-w-2xl leading-[1.8]">{data.intro}</p>
    </Reveal>
    <ol className={`grid grid-cols-1 sm:grid-cols-2 ${data.stages.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-x-6 gap-y-12`} data-testid={`${testId}-grid`}>
      {data.stages.map((s) => (
        <li key={s.stage} className="border-t border-gold/50 pt-5" data-testid={`${testId}-stage-${s.stage}`}>
          <NorwoodStageArt stage={s} className="mb-5" testId={`${testId}-art-${s.stage}`} />
          <span className="font-serif font-light text-gold text-4xl leading-none">{s.stage}</span>
          <h3 className="text-warm-white mt-3">{s.name}</h3>
          <p className="text-warm-white/70 text-sm mt-2 leading-relaxed">{s.text}</p>
        </li>
      ))}
    </ol>
    <p className="text-warm-white/45 text-xs mt-10 leading-relaxed" data-testid={`${testId}-illustration-note`}>{data.note}</p>
  </HairBand>
);

const HairConcern = () => {
  const { slug } = useParams();
  const concern = getConcern("hair", slug);
  if (!concern) return <NotFound />;

  const override = CONCERN_OVERRIDES[`hair/${slug}`] || {};
  const primary = override.member ? getTeamMember(override.member) : pickPrimary(concern.who);
  const offer = override.offer || offerForConcern("hair", slug, primary);
  const image = concernImage("hair", slug);
  const faqs = fillFaqs(concern.faqs, concern.who, offer);
  const path = concernPath("hair", slug);
  const crumbs = HAIR_BREADCRUMBS.hairLoss(concern.title, path);
  const related = [
    ...HAIR_TREATMENT_CARDS.map((t) => ({ to: t.to, label: t.title })),
    ...(TREATMENTS.filter((t) => t.slug === "threads-and-regenerative").map((t) => ({ to: `/treatments/${t.slug}`, label: t.title }))),
    ...(override.links || []),
  ].filter((item, i, all) => all.findIndex((o) => o.to === item.to) === i);
  const extra = slug === "male-pattern" ? MALE_PATTERN_EXTRA : null;
  const female = slug === "female-thinning" ? FEMALE_THINNING_EXTRA : null;

  return (
    <>
      <SEO
        title={`${concern.title} | Lumière Hair Clinic, Cottesloe`}
        description={`${concern.standfirst} Diagnosis first at Lumière Hair Clinic, Cottesloe — what can be done, what can't, and who to see.`}
        ogImage={image.id}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
            { "@type": "BreadcrumbList", itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `https://www.lumiereaesthetics.com.au${c.to}` })) },
          ],
        }}
      />

      <HairHero eyebrow="Lumière Hair Clinic · Hair loss" title={concern.title} standfirst={concern.standfirst} member={primary} offer={offer} image={image} form={false} cta={false} mobileImage tall={false} testId="hair-concern-hero" />
      <HairBreadcrumb items={crumbs} />

      <HairBand narrow testId="concern-going-on">
        <Reveal>
          <HairEyebrow className="mb-4">What's going on</HairEyebrow>
          <Paras items={concern.going} />
        </Reveal>
        <Reveal className="mt-12">
          <HairEyebrow className="mb-4">Why it happens</HairEyebrow>
          <Bullets items={concern.why} testId="concern-why" />
        </Reveal>
      </HairBand>

      <HairBand dark narrow testId="concern-can-do">
        <Reveal>
          <HairEyebrow className="mb-4">What can realistically be done</HairEyebrow>
          <div className="space-y-6 mt-6">
            {concern.canDo.map((item, i) => (
              <div key={i} className="border-t border-gold/25 pt-5" data-testid={`can-do-${i}`}>
                <h3 className="text-warm-white">{item.name}</h3>
                <p className="text-warm-white/70 text-[15px] mt-2 leading-relaxed">{item.line}</p>
              </div>
            ))}
            <div className="border-t border-gold/25 pt-5" data-testid="concern-cant">
              <h3 className="text-warm-white">What can't</h3>
              <p className="text-warm-white/85 text-[15px] mt-2 leading-relaxed border-l-2 border-gold pl-5">{concern.cant}</p>
            </div>
          </div>
        </Reveal>
      </HairBand>

      <HairBand testId="concern-who">
        <Reveal>
          <HairEyebrow className="mb-6">Who to see</HairEyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
            {concern.who.map((id, i) => (
              <PractitionerCard key={id} member={getTeamMember(id)} index={i} excerpt />
            ))}
          </div>
        </Reveal>
      </HairBand>

      {extra && (
        <>
          <ScaleStages data={extra.norwood} />
          <HairBand narrow testId="male-pattern-why-stage">
            <Reveal>
              <HairEyebrow className="mb-4">{extra.whyStage.heading}</HairEyebrow>
              <Paras items={extra.whyStage.paras} />
            </Reveal>
          </HairBand>
          <HairBand dark testId="male-pattern-pathways">
            <Reveal className="mb-12">
              <HairEyebrow className="mb-3">In sequence</HairEyebrow>
              <HairHeading dark>{extra.pathways.heading}</HairHeading>
            </Reveal>
            <NumberedColumns items={extra.pathways.items} dark testId="male-pattern-pathway" />
          </HairBand>
          <HairBand narrow testId="male-pattern-timing">
            <Reveal>
              <HairEyebrow className="mb-4">{extra.timing.heading}</HairEyebrow>
              <Paras items={extra.timing.paras} />
              <Link to="/journal/understanding-the-norwood-scale" data-testid="norwood-journal-link" className="inline-block mt-6 text-[12px] tracking-[0.16em] uppercase font-medium text-deep-sea hover:text-gold transition-colors duration-300">Read: Understanding the Norwood scale →</Link>
            </Reveal>
          </HairBand>
        </>
      )}

      {female && (
        <>
          <ScaleStages data={female.ludwig} eyebrow={female.ludwig.eyebrow} testId="ludwig" />
          <HairBand narrow testId="female-thinning-why-grade">
            <Reveal>
              <HairEyebrow className="mb-4">{female.whyGrade.heading}</HairEyebrow>
              <Paras items={female.whyGrade.paras} />
              <Link to={female.journal.to} data-testid="ludwig-journal-link" className="inline-block mt-6 text-[12px] tracking-[0.16em] uppercase font-medium text-deep-sea hover:text-gold transition-colors duration-300">{female.journal.label} →</Link>
            </Reveal>
          </HairBand>
        </>
      )}

      <HairBand dark={!extra && !female} sand={!!extra || !!female} narrow testId="concern-first-visit">
        <Reveal><FirstVisitBox offer={offer} /></Reveal>
      </HairBand>

      <HairBand narrow testId="concern-faq">
        <Reveal>
          <HairEyebrow className="mb-4">Questions</HairEyebrow>
          <HairFaq faqs={faqs} testId="faq-item" />
        </Reveal>
      </HairBand>

      <HairBand dark testId="concern-treatments">
        <Reveal>
          <HairEyebrow className="mb-5">Where to go next</HairEyebrow>
          <Chips items={related} />
          <p className="text-warm-white/75 text-[15px] mt-8 leading-relaxed max-w-2xl">
            Considering treatment? Start with a hair assessment — diagnosis first, then the right treatment, which is not always a transplant.{" "}
            <Link to="/hair/assessment" className="text-gold hover:text-hairline font-medium transition-colors duration-300" data-testid="hair-assessment-card-link">Book an assessment</Link>
          </p>
        </Reveal>
      </HairBand>

      <FloatingBar practitioner={primary?.name} hair concern={concern.title} />
      <HairCTABand />
    </>
  );
};

export default HairConcern;
